// The one WebSocket to the office. Messages are JSON objects both ways; their shapes are the office's (src/office/).
/**
 * @param {object} o
 * @param {string | (() => string)} [o.path] '/ws/<office>'; a function is evaluated for each connection
 * @param {() => void} o.onOpen
 * @param {(m: object) => void} o.onMessage
 * @param {(e: CloseEvent, reconnect: () => void) => void} o.onClose
 * @param {() => void} [o.onPong] the office's runtime answered a ping() (a bare "pong", not JSON)
 */
export function createNet({ path = '/ws/main', onOpen, onMessage, onClose, onPong }) {
  let ws = null, want = false, pongAt = 0;
  function connect() {
    want = true;
    const s = ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${typeof path === 'function' ? path() : path}`);
    // A socket given up on (resume) may still close later: only the current one reports.
    s.onopen = (e) => { if (s === ws) onOpen(e); };
    // 4000: a newer tab of mine took over (src/office/); this one stays off, even when it comes back to the front.
    s.onclose = (e) => { if (s !== ws) return; if (e.code === 4000) want = false; onClose(e, retry); };
    s.onmessage = (e) => { if (s !== ws) return; if (e.data === 'pong') { pongAt = performance.now(); onPong?.(); } else onMessage(JSON.parse(e.data)); };
  }
  /** The delayed reconnect after a close: not if something else (resume) has connected meanwhile. */
  const retry = () => { if (want && (!ws || ws.readyState >= 2)) connect(); };
  const send = (m) => ws?.readyState === 1 && ws.send(JSON.stringify(m));
  /** Ask for a "pong"; false if not connected. */
  const ping = () => { if (ws?.readyState !== 1) return false; ws.send('ping'); return true; };
  /** Leave for good (no reconnect). */
  const close = () => { want = false; if (ws) { ws.onclose = null; ws.close(); ws = null; } };
  /** The page is back (from the background, a frozen tab, offline): reconnect now if the socket is gone, and if it
   *  looks open, ping it; no pong within 4 s means it died quietly, so drop it and connect again. */
  function resume() {
    if (!want) return;
    if (!ws || ws.readyState >= 2) return connect();
    if (ws.readyState !== 1) return; // still connecting
    const s = ws, asked = performance.now();
    s.send('ping');
    setTimeout(() => {
      if (s !== ws || !want || s.readyState !== 1 || pongAt >= asked) return; // closed meanwhile (e.g. replaced, 4000): leave it
      ws = null; try { s.close(); } catch {}
      onClose({ code: 4999, reason: 'stale' }, retry); connect(); // 4999: ours, outside the server's 4000–4004
    }, 4000);
  }
  return { connect, send, ping, close, resume };
}
